import { ChangeEvent, useCallback, useEffect, useState } from "react";
import { Dayjs } from "dayjs";
import Grid from "@mui/material/Grid";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import { getAllBranch } from "../../services";
import { PalletRouteRow } from "../../types/pallet-summary.type";
import { Branch } from "../../types/user.type";
import DateRangePicker from "../common/DateRangePicker";
import FilterField, { FILTER_ROW_BOTTOM_SPACING } from "../SaleDepositReport/FilterField";
import { routeLabel } from "./palletSummary";

/** The ทั้งหมด option of both pickers: no branch is every branch, no route is the whole branch. */
const ALL_OPTION = "all";
const ALL_LABEL = "ทั้งหมด";

type RouteOption = Pick<PalletRouteRow, "route_uuid" | "route_code" | "route_name">;

/**
 * The select's value, or "" while no option carries it — the branch list still loading, or a
 * route with no trip in a newly picked range. MUI warns on a value it has no option for.
 */
const listedValue = (selected: string | null, listed: string[]): string => {
  if (selected === null) return ALL_OPTION;
  return listed.includes(selected) ? selected : "";
};

const optionToScope = (value: string): string | null =>
  value === ALL_OPTION ? null : value;

interface PalletFiltersProps {
  startDay: Dayjs;
  endDay: Dayjs;
  onChangeDates: (start: Dayjs, end: Dayjs) => void;
  canPickBranch: boolean;
  branchCode: string | null;
  onSelectBranch: (branchCode: string | null) => void;
  /** Offered once a branch is in scope; its options are that branch's routes in the range. */
  canPickRoute: boolean;
  routeOptions: RouteOption[];
  routeUuid: string | null;
  onSelectRoute: (routeUuid: string | null) => void;
}

/**
 * วันที่, then สาขา for an all-branch reader, then สายส่ง once a branch is in scope. Picking a
 * branch or a route moves the page to that level, the same as opening its row.
 */
export default function PalletFilters({
  startDay,
  endDay,
  onChangeDates,
  canPickBranch,
  branchCode,
  onSelectBranch,
  canPickRoute,
  routeOptions,
  routeUuid,
  onSelectRoute,
}: Readonly<PalletFiltersProps>) {
  const [branches, setBranches] = useState<Branch[]>([]);

  const loadBranches = useCallback(async () => {
    if (!canPickBranch) return;
    try {
      const { data } = await getAllBranch();
      const loaded: Branch[] = data.data ?? [];
      setBranches(loaded);
    } catch {
      // The interceptor already reported the failure; the picker keeps only ทั้งหมด.
      setBranches([]);
    }
  }, [canPickBranch]);

  useEffect(() => {
    loadBranches();
  }, [loadBranches]);

  const handleChangeBranch = (event: ChangeEvent<HTMLInputElement>) => {
    onSelectBranch(optionToScope(event.target.value));
  };

  const handleChangeRoute = (event: ChangeEvent<HTMLInputElement>) => {
    onSelectRoute(optionToScope(event.target.value));
  };

  const branchValue = listedValue(
    branchCode,
    branches.map((branch) => branch.branch_code),
  );
  const routeValue = listedValue(
    routeUuid,
    routeOptions.map((route) => route.route_uuid),
  );

  return (
    <Grid container spacing={2} mb={FILTER_ROW_BOTTOM_SPACING}>
      <FilterField label="วันที่">
        <DateRangePicker dateFrom={startDay} dateTo={endDay} onChange={onChangeDates} />
      </FilterField>
      {canPickBranch && (
        <FilterField label="สาขา">
          <TextField
            select
            size="small"
            fullWidth
            value={branchValue}
            onChange={handleChangeBranch}
          >
            <MenuItem value={ALL_OPTION}>{ALL_LABEL}</MenuItem>
            {branches.map((branch) => (
              <MenuItem key={branch.uuid} value={branch.branch_code}>
                {branch.branch_code} - {branch.branch_name}
              </MenuItem>
            ))}
          </TextField>
        </FilterField>
      )}
      {canPickRoute && (
        <FilterField label="สายส่ง">
          <TextField
            select
            size="small"
            fullWidth
            value={routeValue}
            onChange={handleChangeRoute}
          >
            <MenuItem value={ALL_OPTION}>{ALL_LABEL}</MenuItem>
            {routeOptions.map((route) => (
              <MenuItem key={route.route_uuid} value={route.route_uuid}>
                {routeLabel(route)}
              </MenuItem>
            ))}
          </TextField>
        </FilterField>
      )}
    </Grid>
  );
}
